import React, { useCallback, useEffect, useRef, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import apiService from "../../services/api.service";
import "./dashboardEventChats.css";

const REFRESH_MS = 3000;

const DashboardEventChats = () => {
  const [chats, setChats] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const messagesRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const response = await apiService.getEventChats();
      const list = response.data || [];
      setChats(list);
      setSelectedId((current) => current || list[0]?.id || null);
    } catch {
      setError("Impossibile caricare le chat evento.");
    }
  }, []);

  const loadSelected = useCallback(async () => {
    if (!selectedId) {
      setSelected(null);
      return;
    }
    try {
      const response = await apiService.getEventChat(selectedId);
      setSelected(response.data);
      setError("");
    } catch {
      setError("Impossibile aggiornare la conversazione.");
    }
  }, [selectedId]);

  useEffect(() => {
    loadList();
    const interval = setInterval(loadList, REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadList]);

  useEffect(() => {
    loadSelected();
    const interval = setInterval(loadSelected, REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadSelected]);

  useEffect(() => {
    const element = messagesRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [selected?.messages?.length]);

  const reply = async (event) => {
    event.preventDefault();
    const message = body.trim();
    if (!message || !selectedId) return;
    setBusy(true);
    try {
      const response = await apiService.replyEventChat(selectedId, message);
      setSelected(response.data);
      setBody("");
      await loadList();
    } catch (err) {
      setError(err.response?.data?.detail || "Invio non riuscito.");
      await loadSelected();
    } finally {
      setBusy(false);
    }
  };

  const closeChat = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const response = await apiService.closeEventChat(selectedId);
      setSelected(response.data);
      await loadList();
    } catch (err) {
      setError(err.response?.data?.detail || "Chiusura chat non riuscita.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dashboard-card dashboard-event-chats">
      <div className="dashboard-card__header">
        <div>
          <h1 className="dashboard-card__title">Chat evento</h1>
          <p className="dashboard-card__subtitle">Conversazioni con gli utenti, ordinate dalla più recente.</p>
        </div>
        <span className="dashboard-count">{chats.length} chat</span>
      </div>
      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
      {!chats.length ? (
        <Alert variant="info">Nessun utente ha ancora aperto una chat.</Alert>
      ) : (
        <div className="dashboard-event-chats__layout">
          <div className="dashboard-event-chats__list" role="list" aria-label="Conversazioni">
            {chats.map((chat) => (
              <button
                type="button"
                role="listitem"
                key={chat.id}
                className={`dashboard-event-chats__list-item${selectedId === chat.id ? " is-active" : ""}`}
                onClick={() => setSelectedId(chat.id)}
              >
                <span className="dashboard-event-chats__list-top">
                  <strong>{chat.user_name}</strong>
                  <small>{chat.updated_at ? new Date(chat.updated_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</small>
                </span>
                <span>{chat.user_email}</span>
                <span>{chat.event_name}</span>
                <em className={chat.can_write ? "is-open" : ""}>{chat.can_write ? "Aperta" : "Terminata"}</em>
              </button>
            ))}
          </div>
          <section className="dashboard-event-chats__conversation" aria-label="Conversazione selezionata">
            {selected && (
              <>
                <header className="dashboard-event-chats__conversation-header">
                  <div>
                    <h2>{selected.user_name}</h2>
                    <p>{selected.user_email} · {selected.event_name}</p>
                  </div>
                  {!selected.is_closed && selected.event_active && (
                    <Button variant="outline-danger" size="sm" disabled={busy} onClick={closeChat}>
                      Termina chat
                    </Button>
                  )}
                </header>
                <div className="dashboard-event-chats__messages" ref={messagesRef} aria-live="polite">
                  {selected.messages.map((message) => (
                    <div
                      key={message.id}
                      className={`dashboard-event-chats__message${message.sender_role === "gestore" ? " is-manager" : ""}`}
                    >
                      <strong>{message.sender_role === "gestore" ? "Tu" : selected.user_name}</strong>
                      <p>{message.body}</p>
                      <time>{new Date(message.created_at).toLocaleString("it-IT")}</time>
                    </div>
                  ))}
                </div>
                {selected.can_write ? (
                  <Form className="dashboard-event-chats__reply" onSubmit={reply}>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      maxLength={4000}
                      value={body}
                      onChange={(event) => setBody(event.target.value)}
                      placeholder="Scrivi una risposta..."
                      aria-label="Risposta all'utente"
                    />
                    <Button type="submit" disabled={busy || !body.trim()}>{busy ? "Invio..." : "Invia"}</Button>
                  </Form>
                ) : (
                  <p className="dashboard-event-chats__closed">Conversazione terminata: non è più possibile inviare messaggi.</p>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default DashboardEventChats;
